import type { JSX } from "react";
import { Row, Stack, Text } from "../../components/ui";
import { Icon } from "../../components/ui";
import type { IconName } from "../../components/ui/Icon";
import type { AvatarColor } from "../shared/types";

export interface AgentChatItemProps {
  readonly title: string;
  readonly preview?: string;
  readonly time?: string;
  readonly iconName?: IconName;
  readonly color?: AvatarColor;
  readonly onAction?: () => void;
  readonly actionIconName?: IconName;
}

const COLOR_CLASS: Record<string, string> = {
  orange: "bg-accent",
  blue: "bg-blue",
  green: "bg-green",
  red: "bg-red",
  purple: "bg-purple",
  pink: "bg-pink",
  gray: "bg-content-tertiary",
};

/**
 * Agent list item — same layout as Email/Telegram:
 * Icon avatar (40px) | Title + Time (row 1) | Preview (row 2)
 * On hover: time is replaced by action button (archive/unarchive).
 */
export function AgentChatItem({
  title,
  preview,
  time,
  iconName,
  color,
  onAction,
  actionIconName,
}: AgentChatItemProps): JSX.Element {
  const bgClass = COLOR_CLASS[color ?? "orange"] ?? "bg-accent";
  return (
    <>
      <div
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white ${bgClass}`}
      >
        <Icon name={iconName ?? "message-circle"} size={16} />
      </div>
      <Stack gap={0.5} flex1>
        <Row justify="between">
          <Text variant="title" truncate as="span" className="list-item-title">
            {title}
          </Text>
          {onAction ? (
            <span className="relative flex shrink-0 items-center">
              {time ? (
                <Text variant="tiny" noShrink as="span" className="list-item-meta group-hover:hidden">
                  {time}
                </Text>
              ) : null}
              <button
                type="button"
                className="hidden cursor-pointer items-center text-content-tertiary hover:text-content-primary group-hover:flex"
                onClick={(e) => {
                  e.stopPropagation();
                  onAction();
                }}
              >
                <Icon name={actionIconName ?? "archive"} size={14} />
              </button>
            </span>
          ) : time ? (
            <Text variant="tiny" noShrink as="span" className="list-item-meta">
              {time}
            </Text>
          ) : null}
        </Row>
        {preview ? (
          <Text variant="caption" truncate className="list-item-secondary">
            {preview}
          </Text>
        ) : null}
      </Stack>
    </>
  );
}
